/**
 * @module editorStore
 * @description Editor Store - Editor State Management for Preparo
 *
 * RESPONSIBILITY: Manages Milkdown editor state and document content
 *
 * This store handles:
 * - Document content (Markdown text)
 * - Editor instance (Milkdown)
 * - Save/load operations for content (to/from IndexedDB)
 * - Editor state (dirty, saving, loading)
 * - Active marks and nodes (for toolbar)
 * - Status messages
 *
 * RELATIONSHIP WITH appStore:
 * - editorStore manages CONTENT (Markdown text)
 * - appStore manages METADATA (document info)
 * - Both are coordinated via stores/index.js
 */

import { writable, derived, get, type Writable, type Readable } from 'svelte/store';
import { editorViewCtx, parserCtx, serializerCtx, type Ctx } from '@milkdown/kit/core';
import { withTransaction, batchUpdates } from './storeTransaction';
import { db } from '../services/indexedDBService';
import { loggers } from '../services/loggerService';
import { markProjectDirty, markPresetDirty } from '../services/gitSyncService';
import { timeouts } from '../config';
import type { Document, Template } from '../types';

// Create scoped logger for editor operations
const log = loggers.editor;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Editor state shape */
export interface EditorStateData {
  /** Loading a document */
  isLoading: boolean;
  /** Save in progress */
  isSaving: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Unsaved changes flag */
  isDirty: boolean;
  /** Potential error message */
  error: string | null;
}

/** Active text marks in current selection */
export interface ActiveMarksData {
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  code: boolean;
}

/** Active node info in current selection */
export interface ActiveNodeData {
  type: string;
  level: number | null;
}

/** Milkdown editor instance type (simplified) */
export interface MilkdownEditor {
  action: <T>(fn: (ctx: unknown) => T) => T;
  destroy: () => void;
}

/** Timer manager interface */
export interface TimerManager {
  set(id: string, callback: () => void, delay: number): void;
  clear(id: string): void;
  clearAll(): void;
  isActive(id: string): boolean;
}

/** Editor actions interface */
export interface EditorActions {
  selectDoc(doc: Document | null): Promise<string | null>;
  loadDoc(docId: string): Promise<string>;
  saveDoc(docId?: string | null, content?: string | null): Promise<void>;
  loadTemplate(template: Template): void;
  saveTemplate(content?: string | null): Promise<void>;
  markDirty(): void;
  reset(): void;
  getContent(): string;
  setContent(markdown: string): void;
}

// ============================================================================
// BASE STORES
// ============================================================================

/**
 * Current Markdown content of the editor
 */
export const editorContent: Writable<string> = writable('');

/**
 * Milkdown editor instance
 */
export const editorInstance: Writable<MilkdownEditor | null> = writable(null);

/**
 * ID of the currently edited document
 */
export const currentDocId: Writable<string | null> = writable(null);

/**
 * Initial content loaded from database (used for editor initialization)
 */
export const loadedContent: Writable<string> = writable('');

/**
 * YAML frontmatter extracted from document content.
 * Shared between MilkdownEditor (strip/restore) and NotesPanel (shorttitle editing).
 * Contains the raw YAML block including --- delimiters, or empty string if none.
 */
export const yamlFrontmatter: Writable<string> = writable('');

/**
 * ID of the currently editing template (null if editing a document)
 */
export const currentTemplateId: Writable<string | null> = writable(null);

/**
 * Editor state
 */
export const editorState: Writable<EditorStateData> = writable({
  isLoading: false,
  isSaving: false,
  lastSaved: null,
  isDirty: false,
  error: null
});

/**
 * Active marks in the current selection
 */
export const activeMarks: Writable<ActiveMarksData> = writable({
  bold: false,
  italic: false,
  strikethrough: false,
  code: false
});

/**
 * Active node in the selection (heading, paragraph, list, etc.)
 */
export const activeNode: Writable<ActiveNodeData> = writable({
  type: 'paragraph',
  level: null
});

// ============================================================================
// DERIVED STORES
// ============================================================================

/**
 * Indicates if there are unsaved changes
 */
export const hasUnsavedChanges: Readable<boolean> = derived(
  editorState,
  $state => $state.isDirty
);

/**
 * Status message for the user interface
 */
export const statusMessage: Readable<string> = derived(
  editorState,
  $state => {
    if ($state.error) return `Error: ${$state.error}`;
    if ($state.isSaving) return 'Saving...';
    if ($state.isLoading) return 'Loading...';
    if ($state.lastSaved) {
      const elapsed = Date.now() - $state.lastSaved.getTime();
      if (elapsed < timeouts.savedStatusDuration) return 'Saved';
      if (elapsed < timeouts.savedRecentlyThreshold) return 'Saved recently';
      return `Saved ${formatRelativeTime($state.lastSaved)}`;
    }
    return '';
  }
);

// ============================================================================
// TIMER MANAGER
// ============================================================================

/**
 * Timer manager to prevent memory leaks
 */
export const timerManager: TimerManager = {
  _timers: new Map<string, ReturnType<typeof setTimeout>>(),

  set(id: string, callback: () => void, delay: number): void {
    this.clear(id);
    const timerId = setTimeout(() => {
      callback();
      (this._timers as Map<string, ReturnType<typeof setTimeout>>).delete(id);
    }, delay);
    (this._timers as Map<string, ReturnType<typeof setTimeout>>).set(id, timerId);
  },

  clear(id: string): void {
    const timerId = (this._timers as Map<string, ReturnType<typeof setTimeout>>).get(id);
    if (timerId) {
      clearTimeout(timerId);
      (this._timers as Map<string, ReturnType<typeof setTimeout>>).delete(id);
    }
  },

  clearAll(): void {
    for (const timerId of (this._timers as Map<string, ReturnType<typeof setTimeout>>).values()) {
      clearTimeout(timerId);
    }
    (this._timers as Map<string, ReturnType<typeof setTimeout>>).clear();
  },

  isActive(id: string): boolean {
    return (this._timers as Map<string, ReturnType<typeof setTimeout>>).has(id);
  }
} as TimerManager & { _timers: Map<string, ReturnType<typeof setTimeout>> };

// ============================================================================
// ACTIONS
// ============================================================================

export const editorActions: EditorActions = {
  /**
   * Select and load a document into the editor
   */
  async selectDoc(doc: Document | null): Promise<string | null> {
    const currentId = get(currentDocId);

    if (!doc) {
      if (currentId !== null) {
        editorActions.reset();
      }
      return null;
    }

    // If switching from template mode, force reload even if same doc id
    const wasEditingTemplate = get(currentTemplateId) !== null;
    currentTemplateId.set(null);

    if (doc.id === currentId && !wasEditingTemplate) {
      log.debug('Doc already loaded, skipping', { docId: doc.id });
      return get(loadedContent);
    }

    log.debug('Document selected', { docId: doc.id, title: doc.title });
    return editorActions.loadDoc(doc.id);
  },

  /**
   * Load a document into the editor
   */
  async loadDoc(docId: string): Promise<string> {
    log.debug('Loading doc', { docId });

    return withTransaction(
      [editorContent, currentDocId, loadedContent, editorState],
      async () => {
        editorState.update(s => ({
          ...s,
          isLoading: true,
          error: null
        }));

        const content = await fetchDocContent(docId);

        editorContent.set(content);
        loadedContent.set(content);
        currentDocId.set(docId);

        editorState.update(s => ({
          ...s,
          isLoading: false,
          isDirty: false,
          error: null
        }));

        log.info('Doc loaded successfully', { docId });
        return content;
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for loadDoc', { docId, error: error.message });
          editorState.update(s => ({
            ...s,
            isLoading: false,
            error: error.message
          }));
        }
      }
    );
  },

  /**
   * Save the current document
   */
  async saveDoc(docId: string | null = null, content: string | null = null): Promise<void> {
    const id = docId || get(currentDocId);
    const markdown = content || get(editorContent);

    if (!id) {
      log.warn('No doc ID provided for save');
      return;
    }

    log.debug('Saving doc', { docId: id });

    return withTransaction(
      [editorState],
      async () => {
        editorState.update(s => ({
          ...s,
          isSaving: true,
          error: null
        }));

        await saveDocContent(id, markdown);

        editorState.update(s => ({
          ...s,
          isSaving: false,
          isDirty: false,
          lastSaved: new Date(),
          error: null
        }));

        log.info('Doc saved successfully', { docId: id });

        // Mark project dirty for git sync
        const { selectedProject } = await import('./appStore');
        const project = get(selectedProject);
        if (project) markProjectDirty(project.id, 'document');

        timerManager.set('clearSavedMessage', () => {
          editorState.update(s => ({
            ...s,
            lastSaved: null
          }));
        }, timeouts.clearSavedMessageDelay);
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for saveDoc', { docId: id, error: error.message });
          editorState.update(s => ({
            ...s,
            isSaving: false,
            error: error.message
          }));
        }
      }
    );
  },

  /**
   * Load a markdown template into the editor
   */
  loadTemplate(template: Template): void {
    const currentTplId = get(currentTemplateId);
    if (template.id === currentTplId) {
      log.debug('Template already loaded, skipping', { templateId: template.id });
      return;
    }

    log.debug('Loading template', { templateId: template.id, filename: template.filename });

    batchUpdates(() => {
      editorContent.set(template.content || '');
      loadedContent.set(template.content || '');
      currentDocId.set(`template:${template.id}`);
      currentTemplateId.set(template.id);
      editorState.set({
        isLoading: false,
        isSaving: false,
        lastSaved: null,
        isDirty: false,
        error: null
      });
    });

    log.info('Template loaded', { templateId: template.id });
  },

  /**
   * Save the current template content
   */
  async saveTemplate(content: string | null = null): Promise<void> {
    const templateId = get(currentTemplateId);
    const markdown = content || get(editorContent);

    if (!templateId) {
      log.warn('No template ID for save');
      return;
    }

    log.debug('Saving template', { templateId });

    return withTransaction(
      [editorState],
      async () => {
        editorState.update(s => ({
          ...s,
          isSaving: true,
          error: null
        }));

        await db.templates.update(templateId, { content: markdown });

        // Update the in-memory templates store to keep it in sync
        // Import dynamically to avoid circular dependency with appStore
        const { templates, selectedProject } = await import('./appStore');
        templates.update(tpls => tpls.map(tpl =>
          tpl.id === templateId ? { ...tpl, content: markdown, updatedAt: new Date().toISOString() } : tpl
        ));

        // Mark project or preset dirty for git sync
        const tpl = get(templates).find((t: Template) => t.id === templateId);
        if (tpl?.presetId) {
          markPresetDirty(tpl.presetId);
        } else {
          const project = get(selectedProject);
          if (project) markProjectDirty(project.id, 'template');
        }

        editorState.update(s => ({
          ...s,
          isSaving: false,
          isDirty: false,
          lastSaved: new Date(),
          error: null
        }));

        log.info('Template saved successfully', { templateId });

        timerManager.set('clearSavedMessage', () => {
          editorState.update(s => ({
            ...s,
            lastSaved: null
          }));
        }, timeouts.clearSavedMessageDelay);
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for saveTemplate', { templateId, error: error.message });
          editorState.update(s => ({
            ...s,
            isSaving: false,
            error: error.message
          }));
        }
      }
    );
  },

  /**
   * Mark content as modified
   */
  markDirty(): void {
    editorState.update(s => ({ ...s, isDirty: true }));
  },

  /**
   * Reset editor state
   */
  reset(): void {
    timerManager.clearAll();

    batchUpdates(() => {
      editorContent.set('');
      loadedContent.set('');
      currentDocId.set(null);
      currentTemplateId.set(null);
      editorState.set({
        isLoading: false,
        isSaving: false,
        lastSaved: null,
        isDirty: false,
        error: null
      });
    });

    log.debug('Editor state reset');
  },

  /**
   * Get the current Markdown content from the editor
   */
  getContent(): string {
    const editor = get(editorInstance);
    if (!editor) {
      log.warn('No editor instance available');
      return get(editorContent);
    }

    try {
      return editor.action((ctx: Ctx) => {
        const serializer = ctx.get(serializerCtx) as (doc: unknown) => string;
        const view = ctx.get(editorViewCtx) as { state: { doc: unknown } };
        return serializer(view.state.doc);
      });
    } catch (error) {
      log.error('Failed to get editor content', { error });
      return get(editorContent);
    }
  },

  /**
   * Set the editor content
   */
  setContent(markdown: string): void {
    const editor = get(editorInstance);
    if (!editor) {
      log.warn('No editor instance available');
      editorContent.set(markdown);
      return;
    }

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx) as {
          state: { tr: unknown; doc: { content: { size: number } } };
          dispatch: (tr: unknown) => void;
        };
        const parser = ctx.get(parserCtx) as (md: string) => { content: unknown };
        const doc = parser(markdown);

        const { state } = view;
        const tr = (state.tr as { replaceWith: (from: number, to: number, content: unknown) => unknown })
          .replaceWith(0, state.doc.content.size, doc.content);
        view.dispatch(tr);
      });

      editorContent.set(markdown);
      log.debug('Editor content updated');
    } catch (error) {
      log.error('Failed to set editor content', { error });
    }
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format relative time (e.g., "2 minutes ago")
 */
function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/**
 * Fetch document content from IndexedDB
 */
async function fetchDocContent(docId: string): Promise<string> {
  try {
    const content = await db.contents.get(docId);
    if (content) {
      log.debug('Content loaded from IndexedDB', { docId, chars: content.length });
      return content;
    }
    return '';
  } catch (error) {
    log.error('Failed to load content for doc', { docId, error });
    return '';
  }
}

/**
 * Save document content to IndexedDB
 */
async function saveDocContent(docId: string, content: string): Promise<void> {
  try {
    await db.contents.set(docId, content);
    log.debug('Content saved to IndexedDB', { docId, chars: content.length });
  } catch (error) {
    log.error('Failed to save content for doc', { docId, error });
    throw error;
  }
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Stores
  editorContent,
  editorInstance,
  currentDocId,
  currentTemplateId,
  loadedContent,
  editorState,
  activeMarks,
  activeNode,

  // Derived stores
  hasUnsavedChanges,
  statusMessage,

  // Actions
  ...editorActions,

  // Utilities
  timerManager
};
